import { useMemo } from 'react'

import { createApiClient, type ApiClient } from '@/lib/api'
import { useAuthStore } from '@/stores/auth.store'

/**
 * Hook that returns a stable `ApiClient` wired to the current auth
 * store.
 *
 * The client itself is a thin closure — recreating it per render
 * would be cheap, but `useMemo([])` keeps its identity stable so
 * effects + TanStack Query keys can list it as a dependency without
 * triggering refetches every render.
 *
 * The client reads `useAuthStore.getState()` on every request
 * (through the `getAccessToken` callback) so it always sees the
 * freshest token, even if Zustand has not yet re-emitted to React.
 * That matters during the auto-refresh dance: the API's retry
 * runs synchronously after the refresh completes, and we want it
 * to pick up the brand-new token without waiting for React's next
 * render cycle.
 */
export function useApi(): ApiClient {
  return useMemo(
    () =>
      createApiClient({
        getAccessToken: () => useAuthStore.getState().accessToken,
        refreshAccessToken: () => useAuthStore.getState().refresh(),
        onAuthFailure: () => useAuthStore.getState().logout(),
      }),
    [],
  )
}
